import { Autocomplete } from "components";
import React, { useEffect, useState } from "react";
import SubCategoryServices from "services/SubCategoryServices";

/**
 * OrganizationMultiSelectDropDown - Organization dropdown with multiple selection support
 *
 * @param {object} props
 * @param {string} props.name - Name of the field (for form handling).
 * @param {string} [props.label] - Label for the field.
 * @param {Array} props.value - Array of selected organization IDs.
 * @param {function} props.onChange - Change handler (event, value) => void.
 * @param {object} [props.filter] - Additional filters for organization API.
 * @param {boolean} [props.required] - Required field.
 * @param {Array} [props.validators] - Validation rules.
 * @param {Array} [props.errorMessages] - Validation error messages.
 * @param {number} [props.limitTags=3] - Limit number of tags shown (default: 3).
 * @param {boolean} [props.disableCloseOnSelect=true] - Keep dropdown open after selection.
 * @returns {JSX.Element}
 */

const SubCategoryMultiSelectDropDown = ({
  name,
  label,
  value = [], // Default to empty array for multiple selection
  onChange,
  filter,
  required = false,
  validators = [],
  errorMessages = [],
  limitTags = 3,
  disableCloseOnSelect = true,
  ...rest
}) => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    ...filter,
    search: null,
    status: "Active",
  });

  // Update filters when the filter prop changes (category_id changes)
  useEffect(() => {
    setFilters({
      ...filter,
      search: null,
      status: "Active",
    });
  }, [filter]);

  useEffect(() => {
    const fetchData = async () => {
      // Only fetch if we have a category_id filter and component is not disabled
      if (!filters.category_id || rest.disabled) {
        setData([]);
        return;
      }

      try {
        const [data] = await Promise.all([
          SubCategoryServices.getAll({ ...filters }),
        ]);
        setData(data?.data?.subcategories?.data || []);
      } catch (error) {
        console.error("Error fetching sub categories:", error);
        setData([]);
      }
    };

    fetchData();
  }, [filters, rest.disabled]);

  // Convert value array of IDs to array of organization objects
  const selectedSubCategories = Array.isArray(value)
    ? data.filter((item) => value.includes(item.id))
    : [];

  return (
    <div>
      <Autocomplete
        name={name}
        // label={label}
        multiple={true} // Enable multiple selection
        limitTags={limitTags} // Pass limitTags as direct prop
        value={selectedSubCategories}
        onChange={(event, newValue) => {
          // Convert selected organizations back to array of IDs
          const selectedIds = Array.isArray(newValue)
            ? newValue.map((org) => org.id)
            : [];

          if (onChange) {
            // Pass both IDs and full objects to the parent
            onChange(event, selectedIds, newValue);
          }
        }}
        options={data}
        getOptionLabel={(option) => option.name || ""}
        getOptionKey={(option) => option.id} // Use ID as unique key
        freeSolo={false}
        required={required}
        validators={validators}
        errorMessages={errorMessages}
        disableCloseOnSelect={disableCloseOnSelect}
        AutocompleteProps={{
          filterSelectedOptions: true, // Hide already selected options
          getOptionKey: (option) => option.id, // Ensure unique keys for options
          disableListWrap: true, // Improve performance
          noOptionsText: !filters.category_id
            ? "Please select a category first"
            : "No subcategories found",
        }}
        {...rest}
        TextFieldProps={{ placeholder: "Select sub categories" }}
      />
    </div>
  );
};

export default SubCategoryMultiSelectDropDown;
